import { isPlatformBrowser } from '@angular/common';
import { AfterViewInit, ChangeDetectionStrategy, Component, ElementRef, Inject, Input, OnChanges, OnDestroy, PLATFORM_ID, SimpleChanges, ViewChild } from '@angular/core';

@Component({
  selector: 'app-html-previewer',
  templateUrl: './html-previewer.component.html',
  styleUrl: './html-previewer.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class HtmlPreviewerComponent implements AfterViewInit, OnChanges, OnDestroy {
  @Input() html = '';
  @Input() baseUrl: string | null = null;
  @ViewChild('previewFrame') previewFrame?: ElementRef<HTMLIFrameElement>;

  private viewReady = false;

  constructor(@Inject(PLATFORM_ID) private platformId: Object) {}

  ngAfterViewInit(): void {
    this.viewReady = true;
    void this.renderPreview();
  }

  ngOnChanges(_changes: SimpleChanges): void {
    if (!this.viewReady) {
      return;
    }

    void this.renderPreview();
  }

  ngOnDestroy(): void {
    this.clearFrame();
  }

  private clearFrame(): void {
    const frame = this.previewFrame?.nativeElement;
    if (frame) {
      frame.srcdoc = this.buildSrcDoc('');
      frame.removeAttribute('src');
    }
  }

  private async renderPreview(): Promise<void> {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    const frame = this.previewFrame?.nativeElement;
    if (!frame) {
      return;
    }

    frame.srcdoc = this.buildSrcDoc(this.html || '');
    frame.removeAttribute('src');
  }

  private buildSrcDoc(html: string): string {
    const trimmedHtml = String(html || '').trim();
    const parser = new DOMParser();
    const parsedDocument = parser.parseFromString(trimmedHtml || '<!doctype html><html><head></head><body></body></html>', 'text/html');

    if (this.baseUrl) {
      let baseElement = parsedDocument.head.querySelector('base');
      if (!baseElement) {
        baseElement = parsedDocument.createElement('base');
        parsedDocument.head.insertBefore(baseElement, parsedDocument.head.firstChild);
      }
      baseElement.setAttribute('href', this.baseUrl);
    }

    if (!parsedDocument.head.querySelector('meta[charset]')) {
      const charsetMeta = parsedDocument.createElement('meta');
      charsetMeta.setAttribute('charset', 'utf-8');
      parsedDocument.head.insertBefore(charsetMeta, parsedDocument.head.firstChild);
    }

    if (!parsedDocument.head.querySelector('meta[name="viewport"]')) {
      const viewportMeta = parsedDocument.createElement('meta');
      viewportMeta.setAttribute('name', 'viewport');
      viewportMeta.setAttribute('content', 'width=device-width, initial-scale=1');
      parsedDocument.head.appendChild(viewportMeta);
    }

    return `<!DOCTYPE html>\n${parsedDocument.documentElement.outerHTML}`;
  }
}
